<template>
  <section
    v-aha-emit-action.view
    name="onboardingAI"
    class="onboarding-container"
  >
    <div
      v-for="(option, optionIndex) in onboardingOptions"
      :key="optionIndex"
      class="options-card"
      @click="handleClickOnboardingOption(option)"
    >
      <div class="options-card__media">
        <img v-if="option.image" :src="option.image" />
        <img class="mobile-image" v-if="option.imageMobile" :src="option.imageMobile" />
        <video
          v-if="option.video"
          class="desktop-video"
          @mouseenter="pause"
          @mouseleave="play"
          width="220"
          :src="option.video"
          autoplay
          muted
          loop
          playsinline
        ></video>
      </div>
      <div class="options-card__name">
        {{ $i18nPresenter.t(option.name) }}
        <img
          width="54"
          v-if="option.isAI"
          class="ai-label"
          src="@/assets/images/onboarding/v4/ai-label.svg"
        >
        <aha-spinner class="ml-1.5" v-if="$store.state.importStatus == 2 && option.type === 'upload'" />
      </div>
      <div v-if="option.hoverImage || option.hoverImageFr" class="options-card__desc-img">
        <img v-if="getPresenterLocale === 'fr'" :src="option.hoverImageFr" />
        <img v-else :src="option.hoverImage" />
      </div>
    </div>
    <section class="separate-section">
      <div class="divider"></div>
      <span class="uppercase">{{ $i18nPresenter.t('or') }}</span>
    </section>
    <AISlideGeneratorSideLeft
      :placeholderCustom="$i18nPresenter.t('Enter a topic for your next presentation…')"
      :isExamplesExpandedByDefault="true"
      :preventTrackingDefault="true"
      :customSamples="sampleList"
      :handleOpenModalDocToQuiz="openModalDocToQuiz"
      @submitPrompt="handleSubmitPrompt"
      @focusAIInput="trackAIFocusInput"
      class="ai-input"
    >
      <template slot="title">
        <h2 class="input-prompt-title">
          {{ $i18nPresenter.t('Create a Quiz in seconds with') }}
          <img class="ai-label" src="@/assets/images/onboarding/v4/ai-label.svg">
        </h2>
      </template>
    </AISlideGeneratorSideLeft>
  </section>
</template>

<script>
// services
import { mapGetters, mapActions, mapState } from 'vuex'

// modules
import constants from '@/constant'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import utils from '@/util'
import CreateNewPresentationMixin from '@/mixins/presentation/CreateNewPresentationMixin'
import AISlideGeneratorSideLeft from '@/components-v2/presentation-editor/new-slide/OpenAI/AISlideGeneratorSideLeft.vue'

const SUBMIT_PROMPT_EVENT_ACTIONS = {
  Enter: 'AI Quiz Generator > Generate',
  'Button clicked': 'AI Quiz Generator > Generate',
  Examples: 'AI Quiz Generator > Example',
}

export default {
  components: {
    AISlideGeneratorSideLeft,
  },
  mixins: [CreateNewPresentationMixin],
  computed: {
    ...mapGetters(['getPresenterLocale']),
    ...mapGetters('onBoarding', [
      'skippedScreening',
      'hasDoneScreening',
    ]),
    ...mapState({ currentUser: (state) => state.currentUser }),
    onboardingOptions() {
      return constants.onboardingv4Options
    },
    sampleList() {
      const userCountry = this.currentUser.country ? this.currentUser.country : 'Singapore'
      const userFirstName = this.currentUser.firstName ? this.currentUser.firstName : 'Lily'
      return [
        `fun facts about ${userCountry}`,
        `introducing ${userFirstName} to the team`,
        'leadership skills training',
      ]
    },
  },
  created() {
    this.$emit('created')
  },
  methods: {
    ...mapActions(['setIsOnboardingV3ShowingAction']),
    play(event) {
      if (!event || !event.target) return
      event.target.play()
    },

    pause(event) {
      if (!event || !event.target) return
      event.target.pause()
    },

    handleClickOnboardingOption(option) {
      if (!option || !option.type) return
      if (option.type == 'create') {
        this.createNewPresentation(constants.Events.TRIGGER.BLANK_FROM_ONBOARDING_V3)
      } else if (option.type == 'upload') {
        this.$store.state.openImportModal = true
      } else if (option.type == 'templates') {
        this.setIsOnboardingV3ShowingAction(false)
        this.$router.push({ name: 'TemplateLibrary' })
      }
      this._trackGAEvent(option)
    },

    handleSubmitPrompt(mode, prompt) {
      this.createNewPresentation(constants.Events.TRIGGER.AI_PROMPT_FROM_ONBOARDING, {
        prompt,
      })

      this.trackAISubmitPrompt(mode)
    },

    trackAISubmitPrompt(mode) {
      const eventAction = SUBMIT_PROMPT_EVENT_ACTIONS[mode]
      const data = {
        eventAction,
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        touchPoint: 'Onboarding screen',
      }

      utils.trackGA4AndMixpanel(eventAction, data)
    },

    trackAIFocusInput() {
      const eventAction = 'AI Quiz Generator > Focus input'
      const data = {
        eventAction,
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        touchPoint: 'Onboarding screen',
      }
      utils.trackGA4AndMixpanel(eventAction, data)
    },

    openModalDocToQuiz() {
      this.$store.state.openImportModal = true
      this.trackOpenModalDocToQuiz()
    },

    trackOpenModalDocToQuiz() {
      const eventAction = 'AI > Attachment icon > Open import modal'
      const data = {
        eventAction,
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        presentationId: null,
      }
      utils.trackGA4AndMixpanel(eventAction, data)
    },

    _trackGAEvent(option) {
      utils.mixpanelTracking(
        EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        'Onboarding v3 > Select option',
        option.name,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.onboarding-container {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1.5rem;

    & h2.input-prompt-title {
      display: block;
      color: white;
      text-align: center;
      line-height: 2rem;
      font-size: 1.5rem;

      @media (max-width: 768px) {
        font-size: 1.25rem;
      }

      & > .ai-label {
        display: inline-block;
        transform: translateY(-2px) translateX(5px);
        @media (max-width: 768px) {
          width: 69px;
        }
      }
    }

    & > .ai-input {
        grid-column: 1 / 4;

        &::v-deep {

          & .aha-textarea-wrapper {
            margin: 0 auto;
            width: 92%;

            @media screen and (max-width: 768px) {
              width: 100%;
            }

            & > textarea {
              padding-left: 34px;
              padding-right: 45px;

              @media (max-width: 768px) {
                height: 4rem;
                min-height: 4rem;
              }
            }
          }

          & .quiz-header-inner {
            padding: 2rem 1rem 1.5rem 1rem;

            @media (max-width: 768px) {
              padding: 1.5rem 1rem;
            }
          }

          & .quiz-header-inner__title {
            justify-content: center;
            padding-block-end: 0.5rem;
            z-index: 1;
            position: relative;
          }

          & .back-search {
            display: none;
          }

          & > .quiz-header {
            padding: 0;
          }

          & .quiz-header-inner__arrow-wrapper {
            display: none;
          }

          & .example-area {
            margin-block-start: 2rem;
            text-align: center;
            white-space: nowrap;
            overflow: auto;

            @media (max-width: 768px) {
              margin-block-start: 1.25rem;
              width: calc(100% + 14px);
            }

            & > * {
              &:first-child {
                margin-left: 0;
              }

              &:last-child {
                margin-right: 14px;
              }

              margin-left: 0.5rem;
            }
          }
        }
    }

    & > .separate-section {
        grid-column: 1 / 4;
        text-align: center;

        @media (max-width: 768px) {
          margin-block: -10px;
        }

        & > span {
          display: inline-block;
          color: #848484;
          font-family: Nunito;
          font-size: 12px;
          transform: translate(0);
          font-weight: 700;
          line-height: 16px;
          letter-spacing: 0.1em;
          padding: 0 2rem;
          background:#F0F4FA;
        }

        & > .divider {
          transform: translateY(11px);
          border-top: 1px solid #DFE3E9;
        }
    }

    & > .options-card {
      @apply bg-base-0 p-3 lg:p-5 cursor-pointer lg:block flex gap-x-4 relative;
      border-radius: 0.75rem;
      box-shadow: 0px 8px 0px #DFE3E9;
      transition: 0.1s ease-in;

      &:last-child {
        @media (max-width: 768px) {
          margin-bottom: 10px;
        }
      }

      @media (max-width: 1023px) {
        grid-column: 1 / 4;
      }

      &:active:not(:disabled) {
        @apply transform translate-y-1.5 shadow-none;
      }

      &:hover:not(:active):not(:disabled) {
        @apply transform translate-y-0.25;
      }

      &:hover {
        box-shadow: 0px 6px 0px #DFE3E9;

        @media (min-width: 1200px) {
          .options-card__desc-img {
            visibility: visible;
            transform: translateY(0);
          }
        }
      }
      .options-card__media {
        @apply lg:w-full sm:w-1/3 w-1/4;

        & > .mobile-image {
          display: none;

          @media screen and (max-width: 1023px) {
            display: block;
          }
        }

        & > .desktop-video {
          display: none;

          @media screen and (min-width: 1024px) {
            display: block;
          }
        }
      }
      .options-card__name {
        @apply font-bold lg:text-center lg:pt-4 flex justify-center items-center;
        font-size: 1.25rem;
        gap: 1ch;

        @media (max-width: 1023px) {
          font-size: 1rem;
        }
      }
      .options-card__desc-img {
        display: none;
        visibility: hidden;
        position: absolute;
        top: calc(100% + 20px);
        right: 0;
        width: max-content;
        height: auto;
        transform: translateY(10px);
        transition: 0.2s transform ease-out;

        @media (min-width: 1024px) {
        display: inline-block;
      }
    }
  }
}

</style>
